import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary } from "@/pages/Senders";
import { GoogleLogo, Stop, ArrowRight, Warning } from "@phosphor-icons/react";

/**
 * BatchConnectModal — the "chained bulk-connect" flow from the spec.
 *
 * Opens ONE popup and cycles it through every email:
 *   1. POST /api/oauth/gmail/start with { project_pk (pinned), login_hint }
 *   2. On success/failure signal, navigate the popup to Google's Logout URL
 *   3. Sleep 3.5s, then navigate to the next consent URL
 *   4. Repeat until list is exhausted OR user hits Stop
 *
 * All rows pinned to the SAME project_pk chosen at batch start so mid-batch
 * rotation can't land on a project whose test_users list doesn't contain
 * the next email.
 */
export default function BatchConnectModal({ onClose, onCompleted }) {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pinnedProjectId, setPinnedProjectId] = useState("");
  const [running, setRunning] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [awaitingContinue, setAwaitingContinue] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const popupRef = useRef(null);
  const rowsRef = useRef(rows);
  const stopRef = useRef(false);
  const finishResolverRef = useRef(null);
  const bcRef = useRef(null);
  const pollCloseRef = useRef(null);

  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { stopRef.current = stopRequested; }, [stopRequested]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/oauth/gmail/projects");
      const healthy = (data.projects || []).filter(
        (p) => p.slots_free > 0 && p.last_test_ok !== false,
      );
      setProjects(healthy);
      if (healthy.length) setPinnedProjectId(healthy[0].id);
    })();
    return () => {
      // cleanup listeners if unmounted mid-batch
      try { bcRef.current?.close(); } catch (_e) {}
      clearInterval(pollCloseRef.current);
      try { popupRef.current?.close(); } catch (_e) {}
    };
  }, []);

  const parseEmails = () => {
    const seen = new Set();
    const list = emailsRaw.split(/[\s,;]+/).map((t) => t.trim().toLowerCase()).filter((t) => {
      if (!t || !t.includes("@")) return false;
      if (seen.has(t)) return false;
      seen.add(t);
      return true;
    });
    return list;
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const start = async () => {
    if (!pinnedProjectId) { toast.error("Pick an OAuth project to pin the batch to"); return; }
    const emails = parseEmails();
    if (!emails.length) { toast.error("Paste at least one email"); return; }
    const project = projects.find((p) => p.id === pinnedProjectId);
    if (project && emails.length > project.slots_free) {
      const ok = confirm(`Pinned project "${project.name}" has ${project.slots_free} free slots but you're batching ${emails.length} emails. Exceeding slots will fail after ${project.slots_free}. Continue anyway?`);
      if (!ok) return;
    }
    setRows(emails.map((email) => ({ email, status: "queued", message: "" })));
    setRunning(true);
    setStopRequested(false);
    stopRef.current = false;

    // Open ONE popup up front — subsequent emails reuse it via location.replace.
    popupRef.current = window.open("about:blank",
      `warmly-gmail-batch-${Date.now()}`,
      "popup=yes,width=520,height=680,noopener=no,noreferrer=no");
    if (!popupRef.current) {
      toast.error("Popup blocked — allow popups for this site");
      setRunning(false);
      return;
    }
    // Register listeners once for the whole batch
    subscribeToPopupSignals();

    try {
      for (let i = 0; i < emails.length; i++) {
        if (stopRef.current) {
          updateRow(i, { status: "skipped", message: "batch stopped by user" });
          for (let j = i + 1; j < emails.length; j++) {
            updateRow(j, { status: "skipped", message: "batch stopped by user" });
          }
          break;
        }
        setCurrentIndex(i);
        updateRow(i, { status: "processing" });
        const outcome = await runOne(emails[i]);
        updateRow(i, outcome);
        // Between emails, log out of Google inside the popup, wait 3.5s, then loop
        // (next iteration navigates to next consent URL via location.replace).
        if (i < emails.length - 1 && !stopRef.current) {
          try {
            popupRef.current?.location.replace("https://accounts.google.com/Logout");
          } catch (_e) {
            /* cross-origin write is allowed even when read isn't */
          }
          await sleep(3500);
          if (popupRef.current?.closed) {
            setAwaitingContinue(true);
            const cont = await new Promise((res) => { finishResolverRef.current = res; });
            finishResolverRef.current = null;
            setAwaitingContinue(false);
            if (!cont) {
              for (let j = i + 1; j < emails.length; j++) {
                updateRow(j, { status: "skipped", message: "popup closed, batch aborted" });
              }
              break;
            }
          }
        }
      }
    } finally {
      cleanup();
      setRunning(false);
      setCurrentIndex(-1);
      onCompleted?.();
    }
  };

  const runOne = (email) => new Promise(async (resolve) => {
    try {
      const { data } = await api.post("/oauth/gmail/start", {
        project_pk: pinnedProjectId,
        login_hint: email,
      });
      // Navigate the reused popup to the consent URL. Even after Google's
      // cross-origin navigation locked reads, cross-origin location.replace
      // remains permitted from the opener.
      try {
        if (popupRef.current?.closed) {
          popupRef.current = window.open(data.consent_url,
            `warmly-gmail-batch-${Date.now()}`,
            "popup=yes,width=520,height=680,noopener=no,noreferrer=no");
        } else {
          popupRef.current.location.replace(data.consent_url);
        }
      } catch (e) {
        resolve({ status: "failed", message: `could not navigate popup: ${e.message}` });
        return;
      }
      // Wait for the callback signal for THIS email specifically
      finishResolverRef.current = (payload) => {
        finishResolverRef.current = null;
        if (!payload) {
          resolve({ status: "failed", message: "no signal from popup" });
          return;
        }
        if (payload.ok) {
          const okEmail = (payload.email || "").toLowerCase();
          if (okEmail && okEmail !== email) {
            resolve({ status: "failed", message: `mismatch: got ${okEmail}, expected ${email}` });
            return;
          }
          resolve({ status: "ok", message: payload.outcome || "connected", email: payload.email });
        } else {
          resolve({ status: "failed", message: payload.message || "failed" });
        }
      };
      // Safety timeout — 4 min per email
      setTimeout(() => {
        if (finishResolverRef.current) {
          finishResolverRef.current(null);
        }
      }, 240000);
    } catch (e) {
      resolve({ status: "failed", message: e?.response?.data?.detail || e.message || "start failed" });
    }
  });

  const subscribeToPopupSignals = () => {
    const onMsg = (ev) => {
      const d = ev?.data;
      if (d && d.type === "gmail-oauth-result" && finishResolverRef.current) finishResolverRef.current(d);
    };
    const onStorage = (ev) => {
      if (ev.key === "warmly-oauth-last" && ev.newValue && finishResolverRef.current) {
        try {
          const d = JSON.parse(ev.newValue);
          if (d.type === "gmail-oauth-result") finishResolverRef.current(d);
        } catch (_e) {}
      }
    };
    window.addEventListener("message", onMsg);
    window.addEventListener("storage", onStorage);
    const bc = new BroadcastChannel("warmly-oauth");
    bc.onmessage = (ev) => {
      if (ev?.data?.type === "gmail-oauth-result" && finishResolverRef.current) finishResolverRef.current(ev.data);
    };
    bcRef.current = bc;
    pollCloseRef.current = setInterval(() => {
      // no-op — popup close is handled inline per-iteration
    }, 1000);
    // Return a cleanup function via cleanup()
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("storage", onStorage);
      try { bc.close(); } catch (_e) {}
      clearInterval(pollCloseRef.current);
    };
  };

  const cleanup = () => {
    try { bcRef.current?.close(); } catch (_e) {}
    clearInterval(pollCloseRef.current);
    try { popupRef.current?.close(); } catch (_e) {}
    popupRef.current = null;
  };

  const stop = () => {
    setStopRequested(true);
    // If we're awaiting a manual "continue" (popup closed mid-batch), resolve false.
    if (awaitingContinue && finishResolverRef.current) {
      finishResolverRef.current(false);
    }
  };

  const continueAfterPopupClosed = () => {
    if (awaitingContinue && finishResolverRef.current) {
      // Reopen popup and resume
      popupRef.current = window.open("about:blank",
        `warmly-gmail-batch-${Date.now()}`,
        "popup=yes,width=520,height=680,noopener=no,noreferrer=no");
      finishResolverRef.current(true);
    }
  };

  const counts = rows.reduce((c, r) => { c[r.status] = (c[r.status] || 0) + 1; return c; }, {});
  const parsedCount = parseEmails().length;

  return (
    <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center p-4" onClick={onClose} data-testid="seed-batch-modal">
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-mono uppercase tracking-wider flex items-center gap-2">
            <GoogleLogo size={18} weight="bold" className="text-[#E1FF00]" />
            Batch Connect Gmail
          </h2>
          <button onClick={onClose} disabled={running} className="text-[#71717A] hover:text-white font-mono text-xs disabled:opacity-30">[×]</button>
        </div>

        {!running && rows.length === 0 && (
          <>
            <div className="mb-4 text-[11px] font-mono text-[#71717A] leading-relaxed">
              Paste up to 100 seed Gmail addresses. One popup cycles through them —
              each email lands on Google's sign-in, then jumps to the next after consent.
              All emails pin to a single OAuth project so their test-users list stays aligned.
              <br />
              <span className="text-[#E1FF00]">Tip:</span> add these same emails to that project's
              "Test users" list in Google Cloud Console FIRST — otherwise consent will fail.
            </div>

            <label className="micro block mb-1">Pinned OAuth project</label>
            <select value={pinnedProjectId} onChange={(e) => setPinnedProjectId(e.target.value)}
              data-testid="seed-batch-project"
              className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-4">
              {projects.length === 0 && <option value="">no healthy projects with free slots</option>}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.slots_free} free / {p.max_test_users}
                </option>
              ))}
            </select>

            <label className="micro block mb-1">Emails ({parsedCount} parsed)</label>
            <textarea value={emailsRaw} onChange={(e) => setEmailsRaw(e.target.value)} rows={8}
              placeholder={"seed1@gmail.com\nseed2@gmail.com\nseed3@gmail.com"}
              data-testid="seed-batch-textarea"
              className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none mb-4" />

            <div className="flex justify-end gap-2">
              <BtnSecondary onClick={onClose} testid="seed-batch-cancel">Cancel</BtnSecondary>
              <BtnPrimary onClick={start} disabled={!parsedCount || !pinnedProjectId} testid="seed-batch-start">
                <GoogleLogo size={13} weight="bold" /> Start batch ({parsedCount})
              </BtnPrimary>
            </div>
          </>
        )}

        {rows.length > 0 && (
          <>
            <div className="grid grid-cols-5 gap-2 mb-4">
              <Tile label="Queued" v={counts.queued || 0} c="#71717A" />
              <Tile label="Working" v={counts.processing || 0} c="#E1FF00" />
              <Tile label="Ok" v={counts.ok || 0} c="#00E5FF" />
              <Tile label="Failed" v={counts.failed || 0} c="#FF3B30" />
              <Tile label="Skipped" v={counts.skipped || 0} c="#71717A" />
            </div>

            <div className="flex gap-2 mb-3">
              {running && !stopRequested && (
                <BtnSecondary onClick={stop} testid="seed-batch-stop">
                  <Stop size={12} weight="bold" /> Stop after current
                </BtnSecondary>
              )}
              {awaitingContinue && (
                <BtnPrimary onClick={continueAfterPopupClosed} testid="seed-batch-continue">
                  <ArrowRight size={12} weight="bold" /> Continue with next email
                </BtnPrimary>
              )}
              {!running && (
                <BtnSecondary onClick={() => { setRows([]); setEmailsRaw(""); }} testid="seed-batch-restart">
                  Start another batch
                </BtnSecondary>
              )}
            </div>

            {awaitingContinue && (
              <div className="border border-[#E1FF00]/40 bg-[#E1FF00]/5 p-3 mb-3 text-[11px] font-mono text-[#E1FF00]">
                <Warning size={12} weight="bold" className="inline mr-1" />
                Popup closed. Click "Continue with next email" to reopen and resume.
              </div>
            )}

            <div className="border border-[#2A2A2A] bg-[#050505] max-h-72 overflow-y-auto" data-testid="seed-batch-rows">
              <table className="dtable w-full">
                <thead><tr><th style={{ width: 30 }}>#</th><th>Email</th><th>Status</th><th>Detail</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={i === currentIndex ? "bg-[#E1FF00]/5" : ""}>
                      <td className="font-mono text-[10px] text-[#71717A]">{i + 1}</td>
                      <td className="font-mono text-xs">{r.email}</td>
                      <td>
                        <StatusChip s={r.status} />
                      </td>
                      <td className="font-mono text-[10px] text-[#71717A] truncate max-w-md" title={r.message}>{r.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <BtnPrimary onClick={onClose} testid="seed-batch-close">Close</BtnPrimary>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ label, v, c }) {
  return (
    <div className="border border-[#2A2A2A] bg-[#050505] p-2">
      <div className="text-[9px] uppercase tracking-wider text-[#71717A] font-mono">{label}</div>
      <div className="font-mono text-lg mt-0.5" style={{ color: c }}>{v}</div>
    </div>
  );
}

function StatusChip({ s }) {
  const colors = { queued: "#71717A", processing: "#E1FF00", ok: "#00E5FF", failed: "#FF3B30", skipped: "#71717A" };
  const c = colors[s] || "#71717A";
  return <span className="inline-block px-2 py-0.5 border text-[10px] font-mono" style={{ color: c, borderColor: `${c}55` }}>{s}</span>;
}
